import React, {createContext, useContext, useState, useEffect} from 'react';
import Fetch from '@dazn/lp-web/src/hooks/fetch';
import {getCountry} from '@dazn/lp-web/src/utils/dazn/Region';
import {IOffers} from '@dazn/lp-web/src/types/offers.types';
import {logoProxy} from '@dazn/lp-web/src/proxies/logo.proxy';
import {LogosDictionary} from '@dazn/lp-web/src/types/logos.type';
import {
    checkIfHermesOfferEnabled,
    checkIfHermesOfferEnabledPerProductGroup,
    checkIfMultiPromoDiscountsEnabled,
    checkIfPageDiscountsEnabled,
    getPageDiscountsProductGroup,
    getPageDiscountsPromoCodes,
    getPromoCodeList
} from '@dazn/lp-web/src/features/getPromoOffers';
import {getPageId} from '@dazn/lp-web/src/utils/pageUtils';
import {ErrorType, trackError} from '@dazn/lp-web/src/utils/trackError';
import {checkIsUserAnonymous, getJWTToken} from '@dazn/lp-web/src/utils';
import {
    checkIsPPVDiscountOfferEnabled,
    hasBillingRouting
} from '../components/Header/Utils/features';

interface ILandingPageContext {
    logos: LogosDictionary | null;
    getLogos: () => void;
    offers: IOffers | null;
    multiplePromoOffers: IOffers[] | null;
    promoOffers: IOffers | null;
    pagePromoCode: string | null;
    getPromoCodeByEntSetId: (entSetId?: string) => string | null;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    modalData: any;
    setModalData: React.Dispatch<React.SetStateAction<any>>;
    verifyGiftCode: any;
    giftCode: any;
}

interface IOffersPathParams {
    productGroup: string;
    billingRouting?: string;
    promoLandipageId?: string;
    IncludeBundleOffers?: string;
}

export const LandingPageContext = createContext<ILandingPageContext | null>(
    null
);

export const isMultiPromoDiscountsEnabled = checkIfMultiPromoDiscountsEnabled();

export const LandingPageProvider: React.FC<{children: React.ReactNode}> = ({
    children
}) => {
    const pageId = getPageId();
    const isPageDiscountsEnabled = checkIfPageDiscountsEnabled();
    const promoProductGroup = getPageDiscountsProductGroup();
    const promoCodesList: string[] = getPromoCodeList();

    const promoCodes = getPageDiscountsPromoCodes();

    const [logos, setLogos] = useState<LogosDictionary | null>(null);
    const [offers, setOffers] = useState<IOffers | null>(null);
    const [promoOffers, setPromoOffers] = useState<IOffers | null>(null);
    const [multiplePromoOffers, setMultiplePromoOffers] = useState<
        IOffers[] | null
    >(null);
    const [isOffersLoading, setIsOffersLoading] = useState(true);
    const [isPromoOffersLoading, setIsPromoOffersLoading] = useState(true);
    const [giftCode, setGiftCode] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState<any>(null);

    async function getLogos() {
        try {
            const {data} = await Fetch({
                serviceKey: 'Spolo',
                version: 'v1',
                path: `/logosets?%24format=json&country=${getCountry().toLocaleUpperCase()}&type=entitlementSet&imageType=png&width=100&height=100`
            });
            const proxiesLogo = logoProxy(data);
            setLogos(proxiesLogo);
        } catch (error: any) {
            trackError(error, {
                errorType: ErrorType.AsynchronousCode
            });
        }
    }

    async function verifyGiftCode(
        GiftCode: string,
        GiftCodeType = 'plain'
    ): Promise<any> {
        const endpoint =
            window.dazn.startupData.ServiceDictionary.VerifyGiftCode.Versions.v3
                .ServicePath;
        const params = {
            GiftCode,
            GiftCodeType
        };

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(params)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            setGiftCode(data);
            return data;
        } catch (error) {
            return {};
        }
    }

    function getOffersPayload({
        productGroup,
        promoLandipageId,
        IncludeBundleOffers = `true`
    }: IOffersPathParams) {
        const payload: any = {
            Platform: 'web',
            Manufacturer: '',
            ProductGroup: productGroup,
            IsTiering: true,
            IncludeBundleOffers: IncludeBundleOffers
        };

        if (promoLandipageId) {
            payload['PromoLandingPageId'] = promoLandipageId;
        }

        return payload;
    }

    function getOffersPath({
        productGroup,
        billingRouting,
        promoLandipageId,
        IncludeBundleOffers = `true`
    }: IOffersPathParams) {
        const searchParams = new URLSearchParams();
        searchParams.append('Platform', 'web');
        searchParams.append('Manufacturer', '');
        searchParams.append('ProductGroup', productGroup);
        searchParams.append('IsTiering', 'true');
        searchParams.append('IncludeBundleOffers', IncludeBundleOffers);

        if (billingRouting)
            searchParams.append('BillingRouting', billingRouting);

        if (promoLandipageId) {
            searchParams.append('PromoLandingPageId', promoLandipageId);
        }

        return searchParams.toString();
    }
    const filterHermesOffers = (offers: IOffers | null) => {
        if (!checkIfHermesOfferEnabled() || checkIsPPVDiscountOfferEnabled()) {
            return offers?.Offers?.filter((offer) => {
                const checkRuleId = offer?.Id?.includes('#rule');
                const hasEmptyRequiredEntitlements = offer?.Conditions?.every(
                    (condition: {requiredEntitlements: string | any[]}) =>
                        condition?.requiredEntitlements &&
                        (condition?.requiredEntitlements.length === 0 ||
                            !condition?.requiredEntitlements)
                );
                if (
                    !checkRuleId ||
                    (checkRuleId &&
                        !hasEmptyRequiredEntitlements &&
                        !checkIsPPVDiscountOfferEnabled())
                )
                    return offer;
            });
        }
        if (checkIfHermesOfferEnabled()) {
            const offerProductGroupMatchingFeatureFlag = offers?.Offers?.filter(
                (offer) => {
                    const offerGroup = offer?.ProductGroup;
                    const effectiveOfferGroup =
                        offerGroup === '' ? 'DAZN' : offerGroup;
                    return checkIfHermesOfferEnabledPerProductGroup(
                        effectiveOfferGroup
                    );
                }
            );

            const filteredDiscountedOffers =
                offerProductGroupMatchingFeatureFlag?.filter((offer) => {
                    const hasDiscount =
                        offer.ChargeTiers.some((tier) => {
                            return tier.Discount;
                        }) || offer.TotalFreeDays > 0;

                    const hasNoEntitlements = offer?.Conditions?.every(
                        (condition) =>
                            condition?.requiredEntitlements &&
                            condition?.requiredEntitlements?.length === 0
                    );

                    const hasProductBundle =
                        offer?.ProductBundle &&
                        offer?.ProductBundle?.ProductItems?.length > 0;

                    return (
                        hasDiscount && hasNoEntitlements && !hasProductBundle
                    );
                }) || [];
            const hermesOffersWithOutDiscount =
                offerProductGroupMatchingFeatureFlag?.filter((offer) => {
                    return !filteredDiscountedOffers.some((discountedOffer) => {
                        return (
                            discountedOffer?.EntitlementSetId ===
                                offer?.EntitlementSetId &&
                            discountedOffer?.BillingPeriod ===
                                offer?.BillingPeriod
                        );
                    });
                }) || [];
            const allRemainingOffers =
                offers?.Offers?.filter((offer: any) => {
                    const checkRuleId = offer?.Id?.includes('#rule');
                    const offerGroup = offer?.ProductGroup;
                    const effectiveOfferGroup =
                        offerGroup === '' ? 'DAZN' : offerGroup;
                    return (
                        !checkIfHermesOfferEnabledPerProductGroup(
                            effectiveOfferGroup
                        ) && !checkRuleId
                    );
                }) || [];

            if (
                offerProductGroupMatchingFeatureFlag &&
                filteredDiscountedOffers?.length === 0
            ) {
                return [
                    ...offerProductGroupMatchingFeatureFlag,
                    ...filteredDiscountedOffers,
                    ...allRemainingOffers,
                    ...hermesOffersWithOutDiscount
                ];
            }
            return [
                ...filteredDiscountedOffers,
                ...allRemainingOffers,
                ...hermesOffersWithOutDiscount
            ];
        }
    };

    useEffect(() => {
        const getOffers = async () => {
            try {
                const {data} = await Fetch({
                    serviceKey: 'RatePlans',
                    version: 'v4',
                    path: `/${getCountry()}?${getOffersPath({
                        productGroup: 'all',
                        billingRouting: hasBillingRouting()
                            ? 'billing2'
                            : undefined
                    })}`
                });
                const filterData = filterHermesOffers(data);
                setOffers({...data, Offers: filterData});
            } catch (error: any) {
                trackError(error, {
                    errorType: ErrorType.AsynchronousCode
                });
            } finally {
                setIsOffersLoading(false);
            }
        };

        const getMultiPromoOffers = async (promoCode?: string) => {
            try {
                if (!isMultiPromoDiscountsEnabled || !promoProductGroup) {
                    return;
                }

                const {data} = await Fetch({
                    serviceKey: 'RatePlans',
                    version: 'v4',
                    ...(checkIsUserAnonymous() && {
                        path: `/${getCountry()}?${getOffersPath({
                            productGroup: promoProductGroup,
                            billingRouting: hasBillingRouting()
                                ? 'billing2'
                                : undefined,
                            promoLandipageId: promoCode,
                            IncludeBundleOffers: `false`
                        })}`,
                        method: 'GET'
                    }),
                    ...(!checkIsUserAnonymous() && {
                        body: getOffersPayload({
                            productGroup: promoProductGroup,
                            promoLandipageId: promoCode,
                            IncludeBundleOffers: `false`
                        }),
                        method: 'POST',
                        headers: {authorization: `Bearer ${getJWTToken()}`}
                    })
                });

                return data;
            } catch (error: any) {
                trackError(error, {
                    errorType: ErrorType.AsynchronousCode
                });
            } finally {
                setIsPromoOffersLoading(false);
            }
        };

        const getPromoOffers = async () => {
            try {
                if (
                    !isPageDiscountsEnabled ||
                    !promoProductGroup ||
                    !Object.keys(promoCodes)?.length ||
                    !Object.keys(promoCodes)?.includes(pageId)
                ) {
                    return;
                }

                const {data} = await Fetch({
                    serviceKey: 'RatePlans',
                    version: 'v4',
                    ...(checkIsUserAnonymous() && {
                        path: `/${getCountry()}?${getOffersPath({
                            productGroup: promoProductGroup,
                            billingRouting: hasBillingRouting()
                                ? 'billing2'
                                : undefined,
                            promoLandipageId: promoCodes[pageId]
                        })}`,
                        method: 'GET'
                    }),
                    ...(!checkIsUserAnonymous() && {
                        body: getOffersPayload({
                            productGroup: promoProductGroup,
                            promoLandipageId: promoCodes[pageId]
                        }),
                        method: 'POST',
                        headers: {authorization: `Bearer ${getJWTToken()}`}
                    })
                });

                setPromoOffers(data);
            } catch (error: any) {
                trackError(error, {
                    errorType: ErrorType.AsynchronousCode
                });
            } finally {
                setIsPromoOffersLoading(false);
            }
        };

        getOffers();

        if (isMultiPromoDiscountsEnabled) {
            setIsPromoOffersLoading(true);

            Promise.all(
                promoCodesList?.map((promoCode) =>
                    getMultiPromoOffers(promoCode)
                )
            )
                .then((data) => {
                    setMultiplePromoOffers(data);
                })
                .catch((error) =>
                    console.error('Error calling promo offers:', error)
                )
                .finally(() => {
                    setIsPromoOffersLoading(false);
                });
        } else {
            getPromoOffers();
        }
    }, [isPageDiscountsEnabled, pageId, promoCodes, promoProductGroup]);

    const pagePromoCode = (() => {
        if (
            promoOffers &&
            promoOffers?.Offers &&
            promoOffers?.Offers.length > 0
        ) {
            return promoCodes?.[pageId];
        }

        return null;
    })();

    const getPromoCodeByEntSetId = (entSetId: string | undefined) => {
        if (!promoOffers || !entSetId) return null;

        const {Offers, Entitlements} = promoOffers;

        const offer = Offers?.find((offer: any) => {
            return offer.EntitlementSetId === entSetId;
        });

        const includedInOffersEntitlements = Entitlements?.some((value) => {
            return value?.entitlementIds?.includes(entSetId);
        });

        if (offer || includedInOffersEntitlements) return promoCodes?.[pageId];

        return null;
    };

    if (isOffersLoading || isPromoOffersLoading) return null;

    return (
        <LandingPageContext.Provider
            value={{
                getLogos,
                logos,
                offers,
                multiplePromoOffers,
                promoOffers,
                pagePromoCode,
                getPromoCodeByEntSetId,
                showModal,
                setShowModal,
                modalData,
                setModalData,
                verifyGiftCode,
                giftCode
            }}
        >
            {children}
        </LandingPageContext.Provider>
    );
};

export const useLandingPagesContext = (): ILandingPageContext => {
    const context = useContext(LandingPageContext);

    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }

    return context;
};
